// PR-MAY17-MAINTENANCE-PRO commit 2 — pro form.
//
// Replaces yesterday's CreateMaintenanceSheet (which was sending stale
// field names — see commit 1). Built around the agreed model:
//   * In-house mechanic, but labor is STILL charged to the vehicle.
//   * total_charged = parts + labor (+ other).
//   * The selected vehicle drives driver auto-fill (from currentDriver)
//     and odometer prefill (from currentOdometerKm).
//
// The form submits ONE POST /api/v1/maintenance/events with the full
// payload (parts/labor/other cost split lands at create time — see
// commit 2 backend change). Photos + receipt uploads happen on the
// detail page (commit 3).

import { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Wrench, Receipt, Camera, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/Sheet';
import { VehiclePicker, type VehicleOption } from '@/components/common/VehiclePicker';
import { apiClient, extractErrorMessage, extractFieldErrors } from '@/lib/api';

const EVENT_TYPES_LABELS: Record<string, string> = {
  oil_change: 'Oil change', oil_filter: 'Oil filter', air_filter: 'Air filter',
  fuel_filter: 'Fuel filter', cabin_filter: 'Cabin filter',
  brake_pads_front: 'Brake pads (front)', brake_pads_rear: 'Brake pads (rear)',
  brake_discs: 'Brake discs', brake_fluid: 'Brake fluid',
  tire_replacement: 'Tire replacement', tire_rotation: 'Tire rotation', tire_puncture: 'Tire puncture',
  battery: 'Battery', alternator: 'Alternator', starter: 'Starter',
  fuel_pump: 'Fuel pump', water_pump: 'Water pump',
  coolant_flush: 'Coolant flush', radiator: 'Radiator', thermostat: 'Thermostat',
  clutch: 'Clutch', gearbox: 'Gearbox', transmission: 'Transmission', differential: 'Differential',
  suspension: 'Suspension', shock_absorber: 'Shock absorber', steering: 'Steering',
  electrical_wiring: 'Electrical wiring', lights: 'Lights',
  ac_compressor: 'AC compressor', ac_recharge: 'AC recharge',
  engine_overhaul: 'Engine overhaul', engine_repair: 'Engine repair', turbo: 'Turbo',
  bodywork: 'Bodywork', paint: 'Paint', glass: 'Glass',
  periodic_inspection: 'Periodic inspection', general_service: 'General service',
  breakdown_recovery: 'Breakdown recovery', accident_repair: 'Accident repair',
  other: 'Other',
};

type PaidBy = 'cash' | 'driver_advance' | 'vendor';

export function MaintenanceProSheet({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [vehicle, setVehicle] = useState<VehicleOption | null>(null);
  const [eventType, setEventType] = useState('oil_change');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high' | 'urgent_breakdown'>('normal');
  const [serviceLocation, setServiceLocation] = useState('');
  const [odometerAtService, setOdometerAtService] = useState('');
  const [partsCost, setPartsCost] = useState('');
  const [laborCost, setLaborCost] = useState('');
  const [description, setDescription] = useState('');
  const [paidBy, setPaidBy] = useState<PaidBy>('cash');
  // Driver-advance payer state. Only required when paidBy === 'driver_advance'.
  const [paidFromAdvanceId, setPaidFromAdvanceId] = useState('');
  const [advances, setAdvances] = useState<Array<{ id: string; code: string; balance: number; driverName: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  // PR-MAY17-MAINTENANCE-PRO commit 3 — after save, transition to a
  // media-upload state (still in the sheet) so the operator can attach
  // a receipt + damage photos before closing. Avoids forcing a
  // navigation to the detail page just to add files.
  const [stage, setStage] = useState<'form' | 'media'>('form');
  const [createdEventId, setCreatedEventId] = useState<string | null>(null);
  const [createdEventCode, setCreatedEventCode] = useState<string | null>(null);
  const [uploadedReceipts, setUploadedReceipts] = useState<number>(0);
  const [uploadedDamage, setUploadedDamage]   = useState<number>(0);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const receiptInputRef = useRef<HTMLInputElement | null>(null);
  const damageInputRef  = useRef<HTMLInputElement | null>(null);

  const total = (Number(partsCost) || 0) + (Number(laborCost) || 0);

  // Prefill odometer from the vehicle's currentOdometerKm. The picker
  // option doesn't carry odometer, so fetch the vehicle row.
  useEffect(() => {
    if (!vehicleId) { setOdometerAtService(''); return; }
    let cancelled = false;
    apiClient.get<{ currentOdometerKm?: number; currentDriver?: { fullNameEn?: string } }>(
      `/api/v1/vehicles/${vehicleId}`,
    ).then((r) => {
      if (cancelled) return;
      if (r.data.currentOdometerKm != null) {
        setOdometerAtService(String(r.data.currentOdometerKm));
      }
    }).catch(() => { /* odometer prefill is best-effort */ });
    return () => { cancelled = true; };
  }, [vehicleId]);

  // When the operator picks driver_advance, load that vehicle's
  // current-driver's open advances so they can pick one.
  useEffect(() => {
    if (paidBy !== 'driver_advance' || !vehicle?.currentDriver?.id) { setAdvances([]); return; }
    apiClient.get<{ items?: Array<{ id: string; code: string; effectiveBalanceSar?: string; driver?: { fullNameEn?: string } }> }>(
      `/api/v1/driver-advances-v2?driverId=${vehicle.currentDriver.id}&status=open&pageSize=20`,
    ).then((r) => {
      setAdvances(
        (r.data.items ?? []).map((a) => ({
          id: a.id,
          code: a.code,
          balance: Number(a.effectiveBalanceSar ?? 0),
          driverName: a.driver?.fullNameEn ?? vehicle.currentDriver?.fullNameEn ?? 'Driver',
        })),
      );
    }).catch(() => setAdvances([]));
  }, [paidBy, vehicle]);

  const fe = (k: string) => fieldErrors[k]?.[0];

  async function uploadReceipt(file: File) {
    if (!createdEventId) return;
    setUploadBusy(true); setUploadError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await apiClient.post(`/api/v1/maintenance/events/${createdEventId}/invoices`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadedReceipts((n) => n + 1);
    } catch (err) {
      setUploadError(extractErrorMessage(err));
    } finally { setUploadBusy(false); }
  }

  async function uploadDamagePhoto(file: File) {
    if (!createdEventId) return;
    setUploadBusy(true); setUploadError(null);
    try {
      // The maintenance photo endpoint expects multipart with a `kind`
      // field; PHOTO_KINDS includes 'damage' for incident shots.
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', 'damage');
      await apiClient.post(`/api/v1/maintenance/events/${createdEventId}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadedDamage((n) => n + 1);
    } catch (err) {
      setUploadError(extractErrorMessage(err));
    } finally { setUploadBusy(false); }
  }

  function finalize() {
    if (createdEventId) onSaved(createdEventId);
  }

  async function submit() {
    setError(null);
    setFieldErrors({});
    if (!vehicleId) { setFieldErrors({ vehicleId: ['Pick a vehicle'] }); return; }
    setBusy(true);
    try {
      const payload: Record<string, unknown> = {
        vehicleId,
        eventType,
        priority,
        ...(description.trim() && { description: description.trim() }),
        ...(serviceLocation.trim() && { serviceLocation: serviceLocation.trim() }),
        ...(odometerAtService && { odometerAtService: Number(odometerAtService) }),
        ...(Number(partsCost) > 0 && { partsCostSar: Number(partsCost) }),
        ...(Number(laborCost) > 0 && { laborCostSar: Number(laborCost) }),
      };
      if (paidBy === 'driver_advance') {
        if (!vehicle?.currentDriver?.id) {
          setFieldErrors({ paidFromAdvanceId: ['Vehicle has no current driver — pick "Cash" or assign a driver first.'] });
          setBusy(false);
          return;
        }
        if (!paidFromAdvanceId) {
          setFieldErrors({ paidFromAdvanceId: ['Pick the advance the driver used'] });
          setBusy(false);
          return;
        }
        payload.paidFromAdvanceId = paidFromAdvanceId;
        payload.payerDriverId = vehicle.currentDriver.id;
        payload.expenseAmountSar = total > 0 ? total : undefined;
      }
      const r = await apiClient.post<{ id: string; code: string }>('/api/v1/maintenance/events', payload);
      // PR-MAY17-MAINTENANCE-PRO commit 3 — transition to the media
      // stage instead of closing immediately so the operator can
      // attach a receipt + damage photos in the same sheet.
      setCreatedEventId(r.data.id);
      setCreatedEventCode(r.data.code);
      setStage('media');
    } catch (err) {
      const fes = extractFieldErrors(err);
      if (Object.keys(fes).length > 0) {
        setFieldErrors(fes);
        setError('Some fields need attention — see below.');
      } else {
        setError(extractErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open onOpenChange={(o) => { if (!o && !busy && !uploadBusy) { stage === 'media' ? finalize() : onClose(); } }}>
      <SheetContent side="right" className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Wrench className="h-5 w-5" /> {stage === 'form' ? 'Log maintenance (pro)' : `Attach media — ${createdEventCode ?? ''}`}</SheetTitle>
        </SheetHeader>
        {stage === 'media' ? (
          <div className="flex-1 overflow-y-auto p-6 space-y-4" data-maintenance-pro-media>
            <p className="text-sm text-muted-foreground">
              Event <span className="font-mono">{createdEventCode}</span> created. Attach a receipt (PDF or photo)
              and any damage photos. You can also do this later from the event detail page.
            </p>

            <div className="rounded-md border border-border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Receipt className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Receipts / invoices</span>
                </div>
                <span className="text-xs text-muted-foreground tabular-nums" data-uploaded-receipts>{uploadedReceipts} attached</span>
              </div>
              <input
                ref={receiptInputRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadReceipt(f); e.target.value = ''; }}
                data-receipt-input
              />
              <Button variant="outline" size="sm" onClick={() => receiptInputRef.current?.click()} disabled={uploadBusy} className="w-full">
                {uploadBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Upload receipt
              </Button>
            </div>

            <div className="rounded-md border border-border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Damage photos</span>
                </div>
                <span className="text-xs text-muted-foreground tabular-nums" data-uploaded-damage>{uploadedDamage} attached</span>
              </div>
              <input
                ref={damageInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDamagePhoto(f); e.target.value = ''; }}
                data-damage-input
              />
              <Button variant="outline" size="sm" onClick={() => damageInputRef.current?.click()} disabled={uploadBusy} className="w-full">
                {uploadBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} Upload damage photo
              </Button>
            </div>

            {uploadError && <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{uploadError}</div>}

            <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400 p-3 text-sm flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
              <div>Event saved. Uploads are optional — close when done.</div>
            </div>
          </div>
        ) : (
        <div className="flex-1 overflow-y-auto p-6 space-y-4" data-maintenance-pro-sheet>
          {/* Vehicle picker — drives driver + odometer prefill. */}
          <div className="space-y-1.5">
            <Label>Vehicle</Label>
            <VehiclePicker
              value={vehicleId}
              onChange={(id, v) => { setVehicleId(id); setVehicle(v); }}
              placeholder="Pick a vehicle…"
            />
            {fe('vehicleId') && <p className="text-xs text-rose-600" data-error-vehicleId>{fe('vehicleId')}</p>}
          </div>

          {/* Driver — auto-filled, read-only. */}
          {vehicle && (
            <div className="text-xs text-muted-foreground">
              Current driver: <span className="font-medium text-foreground">{vehicle.currentDriver?.fullNameEn ?? '— none —'}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="type">Type</Label>
              <select id="type" value={eventType} onChange={(e) => setEventType(e.target.value)}
                className={`flex h-11 w-full rounded-md border bg-background px-3 text-base ${fe('eventType') ? 'border-rose-500' : 'border-input'}`}
                data-field-eventType>
                {Object.entries(EVENT_TYPES_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              {fe('eventType') && <p className="text-xs text-rose-600">{fe('eventType')}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="prio">Priority</Label>
              <select id="prio" value={priority} onChange={(e) => setPriority(e.target.value as typeof priority)}
                className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent_breakdown">Urgent breakdown</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="odo">Odometer (km)</Label>
              <Input id="odo" type="number" min="0" value={odometerAtService} onChange={(e) => setOdometerAtService(e.target.value)}
                className={fe('odometerAtService') ? 'border-rose-500' : ''} data-field-odometer />
              {fe('odometerAtService') && <p className="text-xs text-rose-600">{fe('odometerAtService')}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="loc">Service location</Label>
              <Input id="loc" value={serviceLocation} onChange={(e) => setServiceLocation(e.target.value)} maxLength={40}
                placeholder="e.g. In-house workshop" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Cost (in-house mechanic; labor still charged to vehicle)</Label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Input type="number" step="0.01" min="0" value={partsCost} onChange={(e) => setPartsCost(e.target.value)}
                  placeholder="Parts SAR" className={fe('partsCostSar') ? 'border-rose-500' : ''} data-field-partsCost />
                {fe('partsCostSar') && <p className="text-xs text-rose-600">{fe('partsCostSar')}</p>}
              </div>
              <div>
                <Input type="number" step="0.01" min="0" value={laborCost} onChange={(e) => setLaborCost(e.target.value)}
                  placeholder="Labor SAR" className={fe('laborCostSar') ? 'border-rose-500' : ''} data-field-laborCost />
                {fe('laborCostSar') && <p className="text-xs text-rose-600">{fe('laborCostSar')}</p>}
              </div>
              <div className="rounded-md border border-input bg-muted/40 px-3 py-2 text-sm font-mono tabular-nums flex items-center justify-end" data-total-cost>
                {total.toFixed(2)}
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">Total = parts + labor. Charged to vehicle → flows to investor on rollup.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="paidBy">Paid by</Label>
            <select id="paidBy" value={paidBy} onChange={(e) => setPaidBy(e.target.value as PaidBy)}
              className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base"
              data-field-paidBy>
              <option value="cash">Cash / company funds</option>
              <option value="driver_advance">Driver advance (reimburse from)</option>
              <option value="vendor">Vendor invoice (pay later)</option>
            </select>
          </div>

          {paidBy === 'driver_advance' && (
            <div className="space-y-1.5">
              <Label htmlFor="adv">Advance to reimburse from</Label>
              <select id="adv" value={paidFromAdvanceId} onChange={(e) => setPaidFromAdvanceId(e.target.value)}
                className={`flex h-11 w-full rounded-md border bg-background px-3 text-base ${fe('paidFromAdvanceId') ? 'border-rose-500' : 'border-input'}`}>
                <option value="">— Pick advance —</option>
                {advances.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} — {a.driverName} (balance {a.balance.toFixed(2)} SAR)
                  </option>
                ))}
              </select>
              {fe('paidFromAdvanceId') && <p className="text-xs text-rose-600">{fe('paidFromAdvanceId')}</p>}
              {advances.length === 0 && vehicle?.currentDriver?.id && (
                <p className="text-xs text-muted-foreground">No open advances for this driver. Issue an advance first.</p>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="desc">Description</Label>
            <textarea id="desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000}
              className={`flex w-full rounded-md border bg-background px-3 py-2 text-base ${fe('description') ? 'border-rose-500' : 'border-input'}`}
              placeholder="What was done / what failed / observations" />
            {fe('description') && <p className="text-xs text-rose-600">{fe('description')}</p>}
          </div>

          {error && <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm" data-maintenance-pro-error>{error}</div>}
        </div>
        )}
        <SheetFooter>
          {stage === 'form' ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button onClick={submit} disabled={busy || !vehicleId} className="gap-2" data-pro-submit>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save event
              </Button>
            </>
          ) : (
            <Button onClick={finalize} disabled={uploadBusy} className="gap-2" data-pro-finalize>
              <CheckCircle2 className="h-4 w-4" /> Done
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
